import React, { useMemo } from 'react'

import { SxProps } from '@mui/material'

import { useTranslation } from 'language/translator'
import { parseSingleFilterLabel } from 'components_new/organisms/Widget/WidgetSettings/FilterSection/utils'

/* Atoms */
import Box from 'components_new/atoms/Box'

/* Molecules */
import FilterPopover from 'components_new/molecules/FilterPopover'
import SearchAndSelect from 'components_new/molecules/SearchAndSelect'
import { Option, getSortedOptions } from 'components_new/molecules/SelectSearch'

/* API */
import * as UserEvents from 'redux/api/internal/UserEvents'

/* Types */
import { isPublicDashboard, TDashboard } from 'types/GlobalDashboard'
import {
  DashboardFilter,
  ParsedDashboardFilterOptions
} from 'types/GlobalDashboardFilter'
import { Action, Context } from 'types/GlobalUserEvents'

interface CurrentFilterProps {
  dashboard: TDashboard
  dashboardFilter: DashboardFilter
  dashboardFilterOptions: Map<string, ParsedDashboardFilterOptions>
  setDashboardFilter: (value: DashboardFilter) => void
  sx?: SxProps
}

const CurrentFilter = (props: CurrentFilterProps) => {
  const {
    dashboard,
    dashboardFilter,
    dashboardFilterOptions,
    setDashboardFilter
  } = props

  const { t } = useTranslation()

  const filterOptions = useMemo(() => {
    const filterOptionsMap = new Map<string, Option[]>()

    Array.from(dashboardFilterOptions.keys()).forEach((key) => {
      const filter = dashboardFilterOptions.get(
        key
      ) as ParsedDashboardFilterOptions

      filterOptionsMap.set(
        key,
        getSortedOptions(
          filter.options?.map((opt) => ({
            id: opt,
            title: parseSingleFilterLabel(opt, t)
          })) || [],
          false
        )
      )
    })

    return filterOptionsMap
  }, [dashboardFilterOptions])

  const handleChange = (
    filter: ParsedDashboardFilterOptions,
    selectedIds: string[]
  ) => {
    const existingFilter = dashboardFilter.filter(
      (fil) => fil.attribute !== filter.attribute_id
    )

    if (!isPublicDashboard(dashboard)) {
      UserEvents.log({
        action: Action.SELECTION_FILTERED,
        context: Context.FROM_FILTER_MENU,
        dashboard_id: dashboard.id,
        dashboard_group_id: dashboard.dashboard_group_id
      })
    }

    const newDashboardFilters = [
      ...existingFilter,
      ...selectedIds.map((id) => ({
        attribute_option_id: null,
        attribute: filter.attribute_id,
        value: id
      }))
    ]

    setDashboardFilter(newDashboardFilters)
  }

  // success
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '0px',
        flex: '1 1 auto',
        overflowY: 'auto'
      }}
    >
      {Array.from(dashboardFilterOptions.values())
        .sort((a, b) => a.index - b.index)
        .map((filter) => {
          const loading = !filter.options

          const selectedIds = dashboardFilter
            .filter((item) => item.attribute === filter.attribute_id)
            .map((item) => item.value as string)

          const selectedLabel =
            selectedIds.length > 0
              ? selectedIds
                .map((id) => parseSingleFilterLabel(id, t))
                .join(', ')
              : t('global', 'all')

          return (
            <FilterPopover
              active={selectedIds.length > 0}
              key={filter.attribute_id}
              loading={loading}
              popoverWidth={300}
              primary={filter.title}
              secondary={selectedLabel}
              sx={{ mb: 1 }}
              tooltipTitle={filter.title}
              tooltipValue={selectedLabel}
            >
              {filter.multiselect ? (
                <SearchAndSelect
                  autoFocus={true}
                  multiple={true}
                  onChange={(value) => handleChange(filter, value)}
                  options={filterOptions.get(filter.attribute_id) || []}
                  size="small"
                  sx={{ p: 2 }}
                  value={selectedIds}
                />
              ) : (
                <SearchAndSelect
                  autoFocus={true}
                  multiple={false}
                  onChange={(value) =>
                    handleChange(filter, value ? [value] : [])
                  }
                  options={filterOptions.get(filter.attribute_id) || []}
                  size="small"
                  sx={{ p: 2 }}
                  value={selectedIds.length === 1 ? selectedIds[0] : null}
                />
              )}
            </FilterPopover>
          )
        })}
    </Box>
  )
}

export default CurrentFilter
